import { http } from "./client";
import type {
  AuthResponse,
  Board,
  Comment,
  Epic,
  MessageResponse,
  Team,
  Ticket,
  TicketState,
  TicketType,
} from "./types";

export const authApi = {
  signup: (email: string, password: string) =>
    http.post<MessageResponse>("/auth/signup", { email, password }, false),
  login: (email: string, password: string) =>
    http.post<AuthResponse>("/auth/login", { email, password }, false),
  verify: (token: string) =>
    http.get<MessageResponse>("/auth/verify", { token }, false),
  resend: (email: string) =>
    http.post<MessageResponse>("/auth/verify/resend", { email }, false),
  logout: () => http.post<MessageResponse>("/auth/logout", {}),
};

export const teamsApi = {
  list: () => http.get<Team[]>("/teams"),
  create: (name: string) => http.post<Team>("/teams", { name }),
  rename: (id: string, name: string) => http.patch<Team>(`/teams/${id}`, { name }),
  remove: (id: string) => http.del<void>(`/teams/${id}`),
};

export const epicsApi = {
  listByTeam: (teamId: string) => http.get<Epic[]>("/epics", { teamId }),
  create: (teamId: string, title: string) => http.post<Epic>("/epics", { teamId, title }),
  rename: (id: string, title: string) => http.patch<Epic>(`/epics/${id}`, { title }),
  remove: (id: string) => http.del<void>(`/epics/${id}`),
};

export interface CreateTicketInput {
  teamId: string;
  type: TicketType;
  epicId?: string | null;
  title: string;
  body?: string;
}

export interface UpdateTicketInput {
  type: TicketType;
  epicId?: string | null;
  title: string;
  body?: string;
}

export const ticketsApi = {
  get: (id: string) => http.get<Ticket>(`/tickets/${id}`),
  create: (input: CreateTicketInput) => http.post<Ticket>("/tickets", input),
  update: (id: string, input: UpdateTicketInput) => http.patch<Ticket>(`/tickets/${id}`, input),
  changeState: (id: string, state: TicketState) =>
    http.patch<Ticket>(`/tickets/${id}/state`, { state }),
  remove: (id: string) => http.del<void>(`/tickets/${id}`),
  listComments: (id: string) => http.get<Comment[]>(`/tickets/${id}/comments`),
  addComment: (id: string, body: string) =>
    http.post<Comment>(`/tickets/${id}/comments`, { body }),
};

export interface BoardFilters {
  type?: string;
  epic?: string;
  q?: string;
}

export const boardApi = {
  get: (teamId: string, filters: BoardFilters = {}) =>
    http.get<Board>(`/teams/${teamId}/board`, { ...filters } as Record<string, string | undefined>),
};
